import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useActiveReplanningStage } from '~/hooks/useActiveReplanningStage';
import { doseVisibilityState } from '~/state/atoms/dose/doseAtoms';
import {
    showDoseStatisticsTableState,
    showDVHViewComponentState,
} from '~/state/atoms/viewerAtoms';

/**
 * Applies the workflow step's dose display policy to the display atoms.
 * While a dose is selected, the dose overlay is shown by default on the
 * setup, dose and export steps and hidden by default on every other step.
 * The default is written when the selected dose changes and when the step
 * enters or leaves setup, dose or export. Between those moments the
 * visibility is the user's: a dose shown on one of the other steps stays
 * shown while the user moves among those steps. The DVH chart and dose
 * statistics are offered on the dose step only. Inside adaptive
 * replanning the stage decides both instead of the step. The atoms are the
 * display truth; the drawn Environments follow them through the display
 * bridge.
 * Mount once, in a component that stays rendered for as long as the viewer
 * does.
 */
export function useDoseStepDisplayPolicy(): void {
    const currentDose = useCurrentDose();
    const { currentStep } = useWorkflowSteps();
    const stage = useActiveReplanningStage();
    const doseAvailable = !!currentDose;
    // Drawing a dose and analyzing it are separate permissions; setup has only the first.
    const allowedOnCanvas =
        doseAvailable &&
        (stage
            ? stage.showsDose
            : currentStep?.id === 'predictive-planning' ||
              currentStep?.id === 'setup' ||
              currentStep?.id === 'export');
    const showsDoseAnalysis =
        doseAvailable &&
        (stage
            ? stage.showsDoseAnalysis
            : currentStep?.id === 'predictive-planning');
    // Re-writes the default on a move between two steps that both draw the dose.
    const doseDrawingStepId = allowedOnCanvas ? currentStep?.id : undefined;
    const setDoseVisibility = useSetRecoilState(doseVisibilityState);
    const setShowDVHViewComponent = useSetRecoilState(
        showDVHViewComponentState
    );
    const setShowDoseStatisticsTable = useSetRecoilState(
        showDoseStatisticsTableState
    );

    useEffect(() => {
        setDoseVisibility(allowedOnCanvas);

        // DVH & statistics panels: null = disabled, false = hidden, true = shown
        if (!doseAvailable) {
            setShowDVHViewComponent(null);
            setShowDoseStatisticsTable(null);
        } else {
            setShowDVHViewComponent(showsDoseAnalysis);
            setShowDoseStatisticsTable(showsDoseAnalysis);
        }
    }, [
        currentDose,
        doseAvailable,
        allowedOnCanvas,
        showsDoseAnalysis,
        doseDrawingStepId,
        setDoseVisibility,
        setShowDVHViewComponent,
        setShowDoseStatisticsTable,
    ]);
}
